import React, { useEffect, useState, useRef, useCallback } from "react";
import { useFormikContext } from "formik";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import Select from "../../form/Select";
import Input from "../../form/Input";
import { billingDateOptions } from "./utils/billingDateOptions";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import CostCenters from "./CostCenters";
import BillingConfigurationDialog from "./BillingConfigurationDialog";
import BillingSetupQuestionModal from "./BillingSetupQuestionModal";
import BillingTypeSelectionModal from "./BillingTypeSelectionModal";
import BillingMethodSelectionModal from "./BillingMethodSelectionModal";
import useAdmin from "src/hooks/useAdmin";
import useAuth from "../../../hooks/useAuth";
import useMutation from "../../../hooks/useMutation";
import {
  UPDATE_PROJECT_SETTINGS_MUTATION,
  CALCULATE_ALL_BILLING_MUTATION,
} from "../../../constants/graphql/mutations";
import { useParams, useNavigate } from "react-router";
import useQueryParams from "../../../hooks/useQueryParams";
import useNotification from "../../../hooks/notification";

const ADMIN_BILLING_ROLES = ["admin", "manager", "billing_user"];

export default function BillingSettings({ project }) {
  const { values, setFieldValue, submitForm } = useFormikContext();
  const { user } = useAuth();
  const { projectId } = useParams();
  const queryParams = useQueryParams();
  const queryString = queryParams.toString();
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useNotification();
  const [updateProjectSettings] = useMutation(UPDATE_PROJECT_SETTINGS_MUTATION);
  const [calculateAllBilling] = useMutation(CALCULATE_ALL_BILLING_MUTATION);
  const [isRecalculating, setIsRecalculating] = useState(false);

  const isInternalStaff = ADMIN_BILLING_ROLES.includes(user?.role);

  const billingConfigTab = queryParams.get("billingConfig");
  const billingConfigOpen = !!billingConfigTab;

  const setBillingConfigOpen = useCallback(
    isOpenOrTab => {
      const newParams = new URLSearchParams(queryString);
      if (isOpenOrTab) {
        newParams.set("billingConfig", "billing-types");
      } else {
        newParams.delete("billingConfig");
      }
      const search = newParams.toString();
      navigate({
        search: search ? `?${search}` : "",
      });
    },
    [navigate, queryString]
  );

  const handleCloseBillingConfig = useCallback(() => {
    const returnTo = queryParams.get("returnTo");
    if (returnTo && returnTo.startsWith("/")) {
      navigate(returnTo, { replace: true });
      return;
    }
    setBillingConfigOpen(false);
  }, [navigate, queryParams, setBillingConfigOpen]);

  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [billingMethodModalOpen, setBillingMethodModalOpen] = useState(false);
  const [billingTypeModalOpen, setBillingTypeModalOpen] = useState(null);
  const [configuringMultiple, setConfiguringMultiple] = useState(false);
  const [currentConfigStep, setCurrentConfigStep] = useState(0);
  const [pendingBillingTypes, setPendingBillingTypes] = useState({});
  const isTransitioningRef = useRef(false);
  const hasCostCenters = project.costCenters && project.costCenters.length > 0;
  const isAdmin = useAdmin();

  const isAllUnconfigured = () => {
    const billingTypes = project?.billingTypes || {};
    const mainCategories = ["inbound", "outbound", "storage"];
    return mainCategories.every(key => !billingTypes[key]);
  };

  const handleBillingConfigOpen = () => {
    if (!isAdmin) {
      setBillingConfigOpen(true);
      return;
    }

    submitForm();
    if (isAllUnconfigured()) {
      setShowQuestionModal(true);
    } else {
      setBillingConfigOpen(true);
    }
  };

  const handleSelectSame = () => {
    setShowQuestionModal(false);
    setConfiguringMultiple(true);
    setBillingTypeModalOpen("multiple");
  };

  const handleSelectDifferent = () => {
    setShowQuestionModal(false);
    setConfiguringMultiple(false);
    setCurrentConfigStep(1);
    setBillingTypeModalOpen("inbound");
  };

  const handleBillingTypeSelect = async (key, billingTypeData) => {
    // Store the billing type data
    const updatedBillingTypes = {
      ...pendingBillingTypes,
      [key]: billingTypeData,
    };
    setPendingBillingTypes(updatedBillingTypes);

    // If configuring separately, move to next step
    if (!configuringMultiple && currentConfigStep > 0) {
      const categories = ["inbound", "outbound", "storage"];

      if (currentConfigStep < categories.length) {
        isTransitioningRef.current = true;
        setBillingTypeModalOpen(categories[currentConfigStep]);
        setCurrentConfigStep(prev => prev + 1);
      } else {
        // Done with all three, close modal, save to database and show the billing config dialog
        isTransitioningRef.current = false;
        setBillingTypeModalOpen(null);
        setCurrentConfigStep(0);
        setConfiguringMultiple(false);
        await saveBillingTypes(updatedBillingTypes);
        setBillingConfigOpen(true);
      }
    } else {
      // Done configuring, close modal, save and show billing config dialog
      isTransitioningRef.current = false;
      setBillingTypeModalOpen(null);
      setCurrentConfigStep(0);
      setConfiguringMultiple(false);
      await saveBillingTypes(updatedBillingTypes);
      setBillingConfigOpen(true);
    }
  };

  const handleBillingTypeSelectMultiple = async billingTypeData => {
    // Check if billing type contains Time and Materials
    const selectedTypes = Array.isArray(billingTypeData)
      ? billingTypeData
      : billingTypeData?.types || [billingTypeData];

    const containsTimeAndMaterials = selectedTypes.some(
      type =>
        type === "Time and Materials (Blended Rate)" ||
        type === "Time and Materials (Itemized Rates)"
    );

    if (containsTimeAndMaterials) {
      // T&M can't be used for storage (business rule), so apply to inbound/outbound only
      // and prompt user to select storage separately.
      // The modal will transition from "Inbound, Outbound, and Storage Services"
      // to "Storage Services" where T&M options will be filtered out.
      const updatedBillingTypes = {
        inbound: billingTypeData,
        outbound: billingTypeData,
      };
      setPendingBillingTypes(updatedBillingTypes);

      // Switch to separate configuration mode and transition to storage selection
      setConfiguringMultiple(false);
      setCurrentConfigStep(3);
      isTransitioningRef.current = true;
      setBillingTypeModalOpen("storage");
    } else {
      // Apply to all three categories
      const updatedBillingTypes = {
        inbound: billingTypeData,
        outbound: billingTypeData,
        storage: billingTypeData,
      };
      setBillingTypeModalOpen(null);
      setConfiguringMultiple(false);
      // Save to database and show billing config dialog
      await saveBillingTypes(updatedBillingTypes);
      setBillingConfigOpen(true);
    }
  };

  const saveBillingTypes = async billingTypes => {
    await updateProjectSettings({
      variables: {
        projectId,
        billingTypes,
      },
    });
  };

  const handleRecalculateBilling = async () => {
    setIsRecalculating(true);
    try {
      await calculateAllBilling({
        variables: { projectId },
        onFailure: error => {
          notifyError(error?.submit || "Failed to start billing recalculation");
        },
      });
    } catch (error) {
      notifyError("Failed to start billing recalculation");
    } finally {
      setTimeout(() => setIsRecalculating(false), 2000);
    }
  };

  const isAnchorMethod =
    values.billingMethod === "30-Day Cycle Billing" ||
    values.billingMethod === "Anniversary Billing";

  const [anchorMode, setAnchorMode] = useState(values.billingAnchorDate ? "manual" : "auto");
  const [anchorDatePickerOpen, setAnchorDatePickerOpen] = useState(false);
  const prevBillingMethodRef = useRef(values.billingMethod);

  useEffect(() => {
    const prevMethod = prevBillingMethodRef.current;
    prevBillingMethodRef.current = values.billingMethod;

    if (prevMethod === values.billingMethod) return;

    if (values.billingMethod === "Calendar Month Billing" && values.billingDate !== "1") {
      setFieldValue("billingDate", "1");
    }
    if (isAnchorMethod) {
      setAnchorMode("auto");
      setFieldValue("billingAnchorDate", null);
    }
  }, [values.billingMethod]);

  const handleAnchorModeChange = (_, newMode) => {
    if (!newMode) return;
    setAnchorMode(newMode);
    if (newMode === "auto") {
      setFieldValue("billingAnchorDate", null);
    }
  };

  const handleAnchorDateChange = date => {
    if (!date) {
      setAnchorMode("auto");
      setFieldValue("billingAnchorDate", null);
      return;
    }
    setFieldValue("billingAnchorDate", moment(date).format("YYYY-MM-DD"));
  };

  if (!isInternalStaff) {
    return null;
  }

  return (
    <Paper>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={500}>Billing Settings</Typography>
            <IconButton
              size="small"
              onClick={handleRecalculateBilling}
              disabled={isRecalculating || !isAdmin}
              title="Recalculate all billing for this project"
              sx={{ ml: 0.5 }}
            >
              {isRecalculating ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
        <Grid container spacing={1} sx={{ pt: 1.5 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleBillingConfigOpen}
              sx={{ height: "40px" }}
            >
              Billing Configuration
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => isAdmin && setBillingMethodModalOpen(true)}
              disabled={!isAdmin}
              sx={{
                height: "40px",
                justifyContent: "flex-start",
                textTransform: "none",
                fontWeight: 400,
                fontSize: "0.9375rem",
                color: values.billingMethod ? "text.primary" : "text.secondary",
                borderColor: "rgba(0,0,0,0.23)",
                "&:hover": { borderColor: "text.primary" },
                position: "relative",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: -8,
                  left: 8,
                  backgroundColor: "white",
                  px: 0.5,
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  lineHeight: 1,
                }}
              >
                Billing Cycle
              </Typography>
              {values.billingMethod || "Select method"}
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            {isAnchorMethod ? (
              anchorMode === "auto" ? (
                <Box sx={{ position: "relative" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: "absolute",
                      top: -8,
                      left: 8,
                      backgroundColor: "white",
                      px: 0.5,
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      zIndex: 1,
                    }}
                  >
                    Billing Cycle Start Date
                  </Typography>
                  <ToggleButtonGroup
                    value={anchorMode}
                    exclusive
                    onChange={handleAnchorModeChange}
                    size="small"
                    disabled={!isAdmin}
                    fullWidth
                    sx={{
                      "& .MuiToggleButton-root": {
                        height: "40px",
                        textTransform: "none",
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    <ToggleButton value="auto">Auto</ToggleButton>
                    <ToggleButton value="manual">Set Date</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ) : (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Billing Cycle Start Date"
                    disabled={!isAdmin}
                    open={anchorDatePickerOpen}
                    onOpen={() => setAnchorDatePickerOpen(true)}
                    onClose={() => setAnchorDatePickerOpen(false)}
                    value={values.billingAnchorDate ? moment(values.billingAnchorDate, "YYYY-MM-DD") : null}
                    onChange={handleAnchorDateChange}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        onClick: () => isAdmin && setAnchorDatePickerOpen(true),
                        sx: { cursor: "pointer" },
                      },
                      field: { clearable: true },
                    }}
                  />
                </LocalizationProvider>
              )
            ) : (
              <Select
                size="small"
                name="billingDate"
                label="Billing Cycle Start Day"
                options={billingDateOptions}
                disabled={!isAdmin || values.billingMethod === "Calendar Month Billing"}
                showNAButton={false}
              />
            )}
          </Grid>
          <Grid size={12}>
            <Input type="text" name="billingNotes" label="Billing Notes" disabled={!isAdmin} showNAButton={false} />
          </Grid>
        </Grid>
        <CostCenters
          readOnly={!isAdmin}
          costCenters={project.costCenters}
        />
      </Box>
      {billingMethodModalOpen && (
        <BillingMethodSelectionModal
          open={true}
          onClose={() => setBillingMethodModalOpen(false)}
          currentValue={values.billingMethod}
          onSelect={value => setFieldValue("billingMethod", value)}
        />
      )}
      {showQuestionModal && (
        <BillingSetupQuestionModal
          open={true}
          onClose={() => setShowQuestionModal(false)}
          onSelectSame={handleSelectSame}
          onSelectDifferent={handleSelectDifferent}
        />
      )}
      {billingTypeModalOpen && billingTypeModalOpen === "multiple" && (
        <BillingTypeSelectionModal
          key="multiple"
          open={true}
          onClose={() => {
            // Only close if we're not transitioning to storage modal (T&M case)
            if (!isTransitioningRef.current) {
              setBillingTypeModalOpen(null);
              setConfiguringMultiple(false);
            } else {
              setTimeout(() => {
                isTransitioningRef.current = false;
              }, 100);
            }
          }}
          currentValue=""
          category="inbound"
          onSelect={handleBillingTypeSelectMultiple}
          enabledBillingTypes={user?.company?.enabledBillingTypes || []}
          categoryDisplayText="Inbound, Outbound, and Storage Services"
        />
      )}
      {billingTypeModalOpen && billingTypeModalOpen !== "multiple" && (
        <BillingTypeSelectionModal
          key={billingTypeModalOpen}
          open={true}
          onClose={() => {
            // Only close if we're not transitioning between categories
            if (!isTransitioningRef.current) {
              setBillingTypeModalOpen(null);
              setCurrentConfigStep(0);
              setPendingBillingTypes({});
            } else {
              setTimeout(() => {
                isTransitioningRef.current = false;
              }, 100);
            }
          }}
          currentValue=""
          category={billingTypeModalOpen}
          onSelect={billingType => handleBillingTypeSelect(billingTypeModalOpen, billingType)}
          enabledBillingTypes={user?.company?.enabledBillingTypes || []}
          categoryDisplayText={
            billingTypeModalOpen === "inbound"
              ? "Inbound Services"
              : billingTypeModalOpen === "outbound"
              ? "Outbound Services"
              : billingTypeModalOpen === "storage"
              ? "Storage Services"
              : null
          }
          inboundBillingTypes={
            billingTypeModalOpen === "storage" ? pendingBillingTypes.inbound : null
          }
        />
      )}
      {billingConfigOpen && (
        <BillingConfigurationDialog readOnly={!isAdmin} onClose={handleCloseBillingConfig} />
      )}
    </Paper>
  );
}
